import { LLM } from '@/types'
import i18next from 'i18next'

import {
  CLAUDE_FABLE_5_1_MODEL_ID,
  CLAUDE_FABLE_5_MODEL_ID,
  CLAUDE_HAIKU_4_5_MODEL_ID,
  CLAUDE_OPUS_4_8_MODEL_ID,
  CLAUDE_OPUS_5_MODEL_ID,
  CLAUDE_SONNET_5_MODEL_ID
} from '@/lib/anthropic/model-capabilities'
import { CATEGORIES } from '../categories'

const ANTHROPIC_PLATFORM_LINK =
  'https://docs.anthropic.com/claude/reference/getting-started-with-the-api'

const DEFAULT_TOOLS = [
  'b3f07a6e-5e01-423e-9f06-ee51830608be',
  'b3f07a6e-5e01-423e-1f05-ee51830608gf'
]

const CORE_CATEGORIES = [
  CATEGORIES.TECHNOLOGY,
  CATEGORIES.SCIENCE,
  CATEGORIES.ACADEMIA,
  CATEGORIES.LEGAL,
  CATEGORIES.FINANCE,
  CATEGORIES.HEALTH,
  CATEGORIES.PROGRAMMING,
  CATEGORIES.PROGRAMMING_SCRIPTING
]

// Anthropic Models (UPDATED 09/30/26) -----------------------------

const CLAUDE_FABLE_5_1: LLM = {
  modelId: CLAUDE_FABLE_5_1_MODEL_ID,
  modelName: 'Claude Fable 5.1',
  provider: 'anthropic',
  hostedId: CLAUDE_FABLE_5_1_MODEL_ID,
  platformLink: ANTHROPIC_PLATFORM_LINK,
  imageInput: true,
  tier: 'ultra',
  tools: true,
  new: true,
  defaultTools: DEFAULT_TOOLS,
  description: () =>
    i18next.t(
      'models.providers.anthropic.claude_fable_5_1.description',
      'Anthropic’s top-tier model for long-horizon agentic coding and research (1M context, 128K output).'
    ),
  pricing: { currency: 'USD', unit: '1M tokens', inputCost: 10, outputCost: 50 },
  categories: [...CORE_CATEGORIES, CATEGORIES.MARKETING]
}

const CLAUDE_OPUS_5: LLM = {
  modelId: CLAUDE_OPUS_5_MODEL_ID,
  modelName: 'Claude Opus 5',
  provider: 'anthropic',
  hostedId: CLAUDE_OPUS_5_MODEL_ID,
  platformLink: ANTHROPIC_PLATFORM_LINK,
  imageInput: true,
  tier: 'ultra',
  tools: true,
  new: true,
  defaultTools: DEFAULT_TOOLS,
  description: () =>
    i18next.t(
      'models.providers.anthropic.claude_opus_5.description',
      'Current Opus for complex agentic coding and enterprise work (1M context, 128K output).'
    ),
  pricing: { currency: 'USD', unit: '1M tokens', inputCost: 5, outputCost: 25 },
  categories: [...CORE_CATEGORIES, CATEGORIES.MARKETING]
}

const CLAUDE_SONNET_5: LLM = {
  modelId: CLAUDE_SONNET_5_MODEL_ID,
  modelName: 'Claude Sonnet 5',
  provider: 'anthropic',
  hostedId: CLAUDE_SONNET_5_MODEL_ID,
  platformLink: ANTHROPIC_PLATFORM_LINK,
  imageInput: true,
  tier: 'pro',
  tools: true,
  new: true,
  defaultTools: DEFAULT_TOOLS,
  description: () => i18next.t('models.providers.anthropic.claude_sonnet_5.description'),
  pricing: { currency: 'USD', unit: '1M tokens', inputCost: 2, outputCost: 10 },
  categories: CORE_CATEGORIES
}

const CLAUDE_HAIKU_4_5: LLM = {
  modelId: CLAUDE_HAIKU_4_5_MODEL_ID,
  modelName: 'Claude Haiku 4.5',
  provider: 'anthropic',
  hostedId: CLAUDE_HAIKU_4_5_MODEL_ID,
  platformLink: ANTHROPIC_PLATFORM_LINK,
  imageInput: true,
  tier: 'pro',
  tools: true,
  defaultTools: DEFAULT_TOOLS,
  description: () => i18next.t('models.providers.anthropic.claude_haiku_4_5.description'),
  pricing: { currency: 'USD', unit: '1M tokens', inputCost: 1, outputCost: 5 },
  categories: [
    CATEGORIES.TECHNOLOGY,
    CATEGORIES.SCIENCE,
    CATEGORIES.ACADEMIA,
    CATEGORIES.FINANCE,
    CATEGORIES.HEALTH,
    CATEGORIES.PROGRAMMING,
    CATEGORIES.PROGRAMMING_SCRIPTING
  ]
}

// Legacy / capacity siblings (still callable; hidden by default in picker)
const CLAUDE_FABLE_5: LLM = {
  modelId: CLAUDE_FABLE_5_MODEL_ID,
  modelName: 'Claude Fable 5',
  provider: 'anthropic',
  hostedId: CLAUDE_FABLE_5_MODEL_ID,
  platformLink: ANTHROPIC_PLATFORM_LINK,
  imageInput: true,
  tier: 'ultra',
  tools: true,
  new: false,
  defaultTools: DEFAULT_TOOLS,
  description: () =>
    i18next.t(
      'models.providers.anthropic.claude_fable_5.description',
      'Previous Fable generation — prefer Claude Fable 5.1 for new work.'
    ),
  pricing: { currency: 'USD', unit: '1M tokens', inputCost: 10, outputCost: 50 },
  categories: [...CORE_CATEGORIES, CATEGORIES.MARKETING]
}

const CLAUDE_OPUS_4_8: LLM = {
  modelId: CLAUDE_OPUS_4_8_MODEL_ID,
  modelName: 'Claude Opus 4.8',
  provider: 'anthropic',
  hostedId: CLAUDE_OPUS_4_8_MODEL_ID,
  platformLink: ANTHROPIC_PLATFORM_LINK,
  imageInput: true,
  tier: 'ultra',
  tools: true,
  new: false,
  defaultTools: DEFAULT_TOOLS,
  description: () => i18next.t('models.providers.anthropic.claude_opus_4_8.description'),
  pricing: { currency: 'USD', unit: '1M tokens', inputCost: 5, outputCost: 25 },
  categories: [...CORE_CATEGORIES, CATEGORIES.MARKETING]
}

const CLAUDE_SONNET_4_5: LLM = {
  modelId: 'claude-sonnet-4-5-20250929',
  modelName: 'Claude Sonnet 4.5',
  provider: 'anthropic',
  hostedId: 'claude-sonnet-4-5-20250929',
  platformLink: ANTHROPIC_PLATFORM_LINK,
  imageInput: true,
  tier: 'pro',
  tools: true,
  new: false,
  defaultTools: DEFAULT_TOOLS,
  description: () => i18next.t('models.providers.anthropic.claude_sonnet_4_5.description'),
  pricing: { currency: 'USD', unit: '1M tokens', inputCost: 3, outputCost: 15 },
  categories: CORE_CATEGORIES
}

export const ANTHROPIC_LLM_LIST: LLM[] = [
  CLAUDE_FABLE_5_1,
  CLAUDE_OPUS_5,
  CLAUDE_SONNET_5,
  CLAUDE_HAIKU_4_5,
  CLAUDE_FABLE_5,
  CLAUDE_OPUS_4_8,
  CLAUDE_SONNET_4_5
]
